import React, { useState } from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import MarketplaceListingPage from '../components/Succeedo/Marketplace/MarketplaceListingPage';

const LISTING_QUERY = gql`
  query MarketplaceListing($id: ID!) {
    currentUser {
      id
      name
      succeedo_persona
    }
    deal_listing(id: $id) {
      id
      industry
      province
      years_in_operation
      revenue
      adjusted_ebitda
      employees
      asking_price
      deal_preferences
      timeline
      business_story
      key_strengths
      revenue_range_bucket
      employee_count_bucket
      photo_urls
      deal_state
      eoi_state
      nda_active
      revealed_business_name
      revealed_owner_name
      revealed_address
      is_owner
    }
  }
`;

const EXPRESS_INTEREST = gql`
  mutation ExpressInterest(
    $deal_id: ID!
    $interest_message: String
    $budget_readiness: String
    $timeline_alignment: String
  ) {
    express_interest(
      deal_id: $deal_id
      interest_message: $interest_message
      budget_readiness: $budget_readiness
      timeline_alignment: $timeline_alignment
    ) {
      introduction_request
      errors
    }
  }
`;

const BUYER_SIGN_NDA = gql`
  mutation BuyerSignNda($deal_id: ID!) {
    buyer_sign_nda(deal_id: $deal_id) {
      success
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function ListingLoader({ listingId }) {
  const [serverErrors, setServerErrors] = useState([]);

  const { data, loading, refetch } = useQuery(LISTING_QUERY, {
    variables: { id: listingId },
    fetchPolicy: 'cache-and-network',
  });

  const [expressMutation, { loading: expressing }] = useMutation(EXPRESS_INTEREST);
  const [signNdaMutation] = useMutation(BUYER_SIGN_NDA);

  const onExpressInterest = async ({ interestMessage, budgetReadiness, timelineAlignment }) => {
    setServerErrors([]);
    try {
      const { data: result } = await expressMutation({
        variables: {
          deal_id: listingId,
          interest_message: interestMessage,
          budget_readiness:  budgetReadiness,
          timeline_alignment: timelineAlignment,
        },
      });
      const errors = result?.express_interest?.errors || [];
      if (errors.length) {
        setServerErrors(errors);
        return { ok: false };
      }
      try { window.ahoy?.track('succeedo_marketplace_eoi_submitted', { listing_id: listingId }); } catch (_e) {}
      refetch();
      return { ok: true };
    } catch (e) {
      setServerErrors([e.message || 'Submit failed']);
      return { ok: false };
    }
  };

  const onSignNda = async () => {
    try {
      const { data: result } = await signNdaMutation({ variables: { deal_id: listingId } });
      if (result?.buyer_sign_nda?.errors?.length) {
        alert(result.buyer_sign_nda.errors.join('\n'));
        return;
      }
      try { window.ahoy?.track('succeedo_marketplace_buyer_signed_nda', { listing_id: listingId }); } catch (_e) {}
      refetch();
    } catch (e) {
      alert(e.message || 'Failed to record NDA signature');
    }
  };

  const user    = data?.currentUser;
  const listing = data?.deal_listing;

  // If the viewer is the owner, bounce them to the seller hub view of
  // their listing — the buyer-side page is the wrong surface for owners.
  React.useEffect(() => {
    if (listing?.is_owner) {
      window.location.href = `/succeedo/deal-room/${listing.id}`;
    }
  }, [listing?.is_owner, listing?.id]);

  return (
    <MarketplaceListingPage
      listing={listing}
      loading={loading}
      buyerProfile={null}
      persona={(user?.succeedo_persona || 'buyer').toUpperCase()}
      userName={user?.name || ''}
      userInitials={deriveInitials(user?.name)}
      expressing={expressing}
      expressErrors={serverErrors}
      onExpressInterest={onExpressInterest}
      onSignNda={onSignNda}
    />
  );
}

export default function SucceedoMarketplaceListingContainer({ listingId }) {
  return (
    <ApolloProvider client={client}>
      <ListingLoader listingId={listingId} />
    </ApolloProvider>
  );
}
